/**
 * OrangeScoreBadge — the ONE Orange Score visual across HQ.
 *
 * Fruit system (canonical assets, defined in this file only):
 *   0–79  RED_ORANGE   → 🍅 Tomato
 *   80–89 ORANGE       → 🍌 Banana bunch (3 bananas)
 *   90–100 GREEN_ORANGE → 🍊 Orange (OrangeBySite fruit)
 *   NOT_SCANNED         → dashed neutral circle
 *
 * IMPORTANT — presentation only. All backend classification keys, thresholds
 * and score persistence remain untouched.
 *
 * Score number is ALWAYS bold BLACK with a white halo stroke so it stays
 * legible on every fruit (light + dark backgrounds).
 *
 * Only size varies between contexts. Reuse this component everywhere —
 * do NOT redraw the fruits inline anywhere else in the codebase.
 */
import React, { useId } from "react";

export function classifyScore(score) {
  if (score == null || Number.isNaN(Number(score))) return "NOT_SCANNED";
  const s = Number(score);
  if (s <= 79) return "RED_ORANGE";
  if (s <= 89) return "ORANGE";
  return "GREEN_ORANGE";
}

// User-facing labels — the internal classification keys are unchanged.
export const CLASSIFICATION_LABEL = {
  RED_ORANGE:   "NEEDS IMPROVEMENT",
  ORANGE:       "GOOD",
  GREEN_ORANGE: "EXCELLENT",
  NOT_SCANNED:  "NOT SCANNED",
};

const CLASSIFICATION_TOOLTIP = {
  RED_ORANGE:   "0–79 · Needs improvement",
  ORANGE:       "80–89 · Good, with opportunities",
  GREEN_ORANGE: "90–100 · Excellent",
  NOT_SCANNED:  "Not scanned",
};

const SIZE = {
  sm: { px: 52, font: 20 },
  md: { px: 68, font: 26 },
  lg: { px: 148, font: 54 },
};

// Score text — always bold black with a white halo for legibility on any fruit.
function ScoreText({ score, fontSize, cx = 32, cy = 40 }) {
  const value = score == null ? "—" : String(score);
  return (
    <text x={cx} y={cy + fontSize * 0.35} textAnchor="middle"
          fontFamily="Manrope, Inter, Arial, sans-serif"
          fontWeight="900" fontSize={fontSize} fill="#0A0A0A"
          style={{ paintOrder: "stroke",
                   stroke: "rgba(255,255,255,0.85)",
                   strokeWidth: Math.max(2, fontSize * 0.14),
                   letterSpacing: "-0.02em" }}>
      {value}
    </text>
  );
}

// -------------------------------------------------------------------- Tomato
function Tomato({ id, score, fontSize }) {
  return (
    <>
      <defs>
        <radialGradient id={`tom-b-${id}`} cx="0.38" cy="0.32" r="0.78">
          <stop offset="0" stopColor="#FF9773"/>
          <stop offset="0.5" stopColor="#E82C0F"/>
          <stop offset="1" stopColor="#6B1400"/>
        </radialGradient>
        <radialGradient id={`tom-h-${id}`} cx="0.3" cy="0.22" r="0.36">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.9"/>
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id={`tom-leaf-${id}`} cx="0.5" cy="0.3" r="0.6">
          <stop offset="0" stopColor="#3EBB5F"/>
          <stop offset="1" stopColor="#0F5F27"/>
        </radialGradient>
      </defs>
      {/* body — slightly squat, plumper */}
      <ellipse cx="32" cy="42" rx="27" ry="25" fill={`url(#tom-b-${id})`}/>
      {/* body seam highlight */}
      <path d="M32 20 q-1 20 0 44" stroke="rgba(0,0,0,0.10)" strokeWidth="1" fill="none"/>
      {/* highlight */}
      <ellipse cx="23" cy="30" rx="12" ry="9" fill={`url(#tom-h-${id})`}/>
      {/* calyx (star leaves) */}
      <g transform="translate(32 17)">
        <path d="M0 -3 L-8 3 L-5 6 L0 4 L5 6 L8 3 Z" fill={`url(#tom-leaf-${id})`}/>
        <path d="M-11 0 L-7 6 L-4 5 Z" fill={`url(#tom-leaf-${id})`}/>
        <path d="M11 0  L7 6  L4 5 Z"  fill={`url(#tom-leaf-${id})`}/>
        <path d="M-1 -8 L1 -8 L1 -2 L-1 -2 Z" fill="#0F5F27"/>
      </g>
      <ScoreText score={score} fontSize={fontSize} cy={42}/>
    </>
  );
}

// -------------------------------------------------------------------- Banana bunch
// Three curved bananas fanning outward from a shared stem cluster. Score sits
// on the front banana. Rendered pointy-ends-out so the bunch reads clearly at
// small sizes.
function BananaBunch({ id, score, fontSize }) {
  return (
    <>
      <defs>
        <linearGradient id={`ban-fill-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFF39A"/>
          <stop offset="0.5" stopColor="#F5C518"/>
          <stop offset="1" stopColor="#A87200"/>
        </linearGradient>
        <linearGradient id={`ban-shine-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.7"/>
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0"/>
        </linearGradient>
      </defs>

      {/* Shared crown/stem — top-centre */}
      <path d="M30 8 Q32 4 34 8 L36 14 L28 14 Z" fill="#5C3B00"/>
      <path d="M28 13 Q32 11 36 13 L36 16 L28 16 Z" fill="#8A5B00"/>

      {/* Back-left banana — curves down-left */}
      <path d="
        M28 15
        C 20 18 8 26 6 40
        C 6 46 10 50 16 50
        C 20 48 22 42 22 34
        C 22 26 24 20 28 17 Z
      " fill={`url(#ban-fill-${id})`} stroke="#5C3B00" strokeWidth="1.2"
        strokeLinejoin="round"/>
      {/* subtle shine */}
      <path d="M12 26 C 10 32 10 38 12 44" stroke={`url(#ban-shine-${id})`}
            strokeWidth="2" fill="none" strokeLinecap="round"/>

      {/* Back-right banana — curves down-right (mirror) */}
      <path d="
        M36 15
        C 44 18 56 26 58 40
        C 58 46 54 50 48 50
        C 44 48 42 42 42 34
        C 42 26 40 20 36 17 Z
      " fill={`url(#ban-fill-${id})`} stroke="#5C3B00" strokeWidth="1.2"
        strokeLinejoin="round"/>
      <path d="M52 26 C 54 32 54 38 52 44" stroke={`url(#ban-shine-${id})`}
            strokeWidth="2" fill="none" strokeLinecap="round"/>

      {/* Front banana — hangs straight down, largest, score lives on it */}
      <path d="
        M26 16
        C 22 22 18 32 20 46
        C 22 58 30 62 32 62
        C 34 62 42 58 44 46
        C 46 32 42 22 38 16 Z
      " fill={`url(#ban-fill-${id})`} stroke="#5C3B00" strokeWidth="1.4"
        strokeLinejoin="round"/>
      {/* front highlight */}
      <path d="M27 24 C 24 34 24 46 28 56" stroke={`url(#ban-shine-${id})`}
            strokeWidth="2.4" fill="none" strokeLinecap="round"/>
      {/* banana tip shading */}
      <path d="M31 60 Q32 62 33 60" stroke="#5C3B00" strokeWidth="1.4"
            fill="none" strokeLinecap="round"/>

      <ScoreText score={score} fontSize={fontSize} cy={44}/>
    </>
  );
}

// -------------------------------------------------------------------- Orange
function OrangeFruit({ id, score, fontSize }) {
  const cx = 32, cy = 38, r = 26;
  return (
    <>
      <defs>
        <radialGradient id={`ora-b-${id}`} cx="0.35" cy="0.3" r="0.78">
          <stop offset="0" stopColor="#FFD9A8"/>
          <stop offset="0.5" stopColor="#FF8A2E"/>
          <stop offset="1" stopColor="#7A2A00"/>
        </radialGradient>
        <radialGradient id={`ora-h-${id}`} cx="0.3" cy="0.22" r="0.36">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.9"/>
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0"/>
        </radialGradient>
      </defs>
      <rect x={cx - 1.2} y={cy - r - 3} width="2.4" height="5" fill="#5C3B00"/>
      <path d={`M${cx + 1} ${cy - r - 1} q7 -5 13 0 q-4 6 -13 2 Z`} fill="#0FA958" stroke="#0A6B36" strokeWidth="0.6"/>
      <circle cx={cx} cy={cy} r={r} fill={`url(#ora-b-${id})`}/>
      {/* dimple hint */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(0,0,0,0.05)" strokeWidth="0.8"/>
      <circle cx={cx - 8} cy={cy - 9} r={r * 0.5} fill={`url(#ora-h-${id})`}/>
      <ScoreText score={score} fontSize={fontSize} cy={cy}/>
    </>
  );
}

// -------------------------------------------------------------------- Not scanned
function NotScanned({ fontSize }) {
  return (
    <>
      <circle cx="32" cy="38" r="26" fill="none" stroke="#B8B4AC"
              strokeWidth="1.6" strokeDasharray="3.5 3"/>
      <text x="32" y={38 + fontSize * 0.35} textAnchor="middle"
            fontFamily="Manrope, Inter, Arial, sans-serif"
            fontWeight="900" fontSize={fontSize} fill="#B8B4AC">—</text>
    </>
  );
}

export default function OrangeScoreBadge({ score, size = "sm", classification, title, testId }) {
  const cls = classification || classifyScore(score);
  const cfg = SIZE[size] || SIZE.sm;
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const notScanned = cls === "NOT_SCANNED";
  const label = notScanned
    ? "Not scanned"
    : `Orange Score ${score} — ${CLASSIFICATION_LABEL[cls]}`;

  return (
    <span
      data-testid={testId || "orange-score-badge"}
      data-classification={cls}
      data-score={score == null ? "" : String(score)}
      title={title || (notScanned ? "Not scanned" : `${label} · ${CLASSIFICATION_TOOLTIP[cls]}`)}
      className="inline-flex items-center justify-center align-middle"
      style={{ width: cfg.px, height: cfg.px }}
      aria-label={label}
      role="img"
    >
      <svg viewBox="0 0 64 70" width={cfg.px} height={cfg.px} aria-hidden>
        {cls === "RED_ORANGE"   && <Tomato       id={id} score={score} fontSize={cfg.font}/>}
        {cls === "ORANGE"       && <BananaBunch  id={id} score={score} fontSize={cfg.font}/>}
        {cls === "GREEN_ORANGE" && <OrangeFruit  id={id} score={score} fontSize={cfg.font}/>}
        {cls === "NOT_SCANNED"  && <NotScanned   fontSize={cfg.font}/>}
      </svg>
    </span>
  );
}
